/** Resolves to whether the text actually reached the clipboard. */
export async function copyToClipboard(text: string): Promise<boolean> {
    if (!text) return false;

    // A lone URL deliberately stays a bare string: the same iOS URL coercion that linkified
    // notes (NSS-534) is what makes a pasted URL tappable, which the unsupported-browser
    // Copy URL button relies on.
    const html = isLoneHttpUrl(text) ? null : textToHtml(text);

    if (typeof navigator !== 'undefined' && navigator.clipboard) {
        // A pasteboard item whose only representation is a bare string gets URL-coerced by iOS
        // when the text parses as a URI — "SUBJECTIVE:" is a syntactically valid scheme, and the
        // iOS 17+ NSURL parser percent-encodes the rest instead of rejecting it, so Notes pastes
        // the whole note as one link. Writing an explicit text/html representation alongside
        // text/plain gives rich-paste targets something better than the coerced URL.
        if (html !== null && typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
            try {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/plain': new Blob([text], { type: 'text/plain' }),
                        'text/html': new Blob([html], { type: 'text/html' }),
                    }),
                ]);
                return true;
            } catch {
                // Fall through — engines without text/html write support reject here.
            }
        }

        if (navigator.clipboard.writeText) {
            try {
                await navigator.clipboard.writeText(text);
                return true;
            } catch {
                // iOS Safari's Clipboard API rejects here more readily than other engines
                // (stricter user-activation checks), and swallowing that left whatever was
                // already on the pasteboard untouched — pasting looked like it copied something
                // else entirely.
            }
        }
    }

    return legacyCopyToClipboard(text, html);
}

function isLoneHttpUrl(text: string): boolean {
    return /^https?:\/\/\S+$/i.test(text);
}

function textToHtml(text: string): string {
    const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\r\n?|\n/g, '<br>');
    // <br> survives paste targets that strip style attributes, while the pre-wrap wrapper
    // preserves intra-line runs of spaces and tabs where styles do survive. The meta prefix
    // pins UTF-8 for consumers that would otherwise sniff the fragment as windows-1252.
    return `<meta charset="utf-8"><div style="white-space:pre-wrap">${escaped}</div>`;
}

function legacyCopyToClipboard(text: string, html: string | null): boolean {
    if (typeof document === 'undefined') return false;

    const previouslyFocused = document.activeElement;

    const textarea = document.createElement('textarea');
    textarea.value = text;
    // readOnly + 16px keep iOS Safari from flashing the software keyboard and applying its
    // persistent sub-16px focus zoom when the offscreen textarea takes focus.
    textarea.readOnly = true;
    textarea.style.fontSize = '16px';
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    if (html !== null) {
        // Overriding the copy event routes the payload through the browser's DOM-data
        // pasteboard path, which registers text/plain as-is — unlike the bare-selection write,
        // which is where iOS attaches the URL representation that made pasted notes render as
        // links (NSS-534).
        textarea.addEventListener(
            'copy',
            (event) => {
                if (!event.clipboardData) return;
                event.clipboardData.setData('text/plain', text);
                event.clipboardData.setData('text/html', html);
                event.preventDefault();
            },
            { once: true },
        );
    }
    document.body.appendChild(textarea);

    try {
        textarea.focus();
        textarea.select();
        return document.execCommand('copy');
    } catch {
        return false;
    } finally {
        document.body.removeChild(textarea);
        // execCommand('copy') requires focusing the offscreen textarea, which otherwise
        // strands keyboard/screen-reader focus on document.body once it's removed.
        if (previouslyFocused instanceof HTMLElement) {
            previouslyFocused.focus();
        }
    }
}
